import { LoyaltyService } from "./services/loyalty/loyalty";
import type {
  TrackEventPayload,
  TrackEventResponse,
} from "./services/loyalty/types";
import type { SDKActionRegistryPayload } from "./state/sdk-action-registry";
import { SDKState } from "./state/sdk-state";
import { SDKOptions } from "./state/sdk-options";
import { SDKTracker } from "./utils/sdk-tracker";
import { ToastNotificationManager } from "./ui/toast-notification-manager";
import { showLoyaltyToastNotification } from "./services/loyalty/show-loyalty-toast-notification";
import {
  EMBED_CONTAINER_ID,
  EMBED_ELEMENT_ID,
  EmbedService,
} from "./services/embed/embed";
import { AuthService } from "./services/auth/auth";
import { ApiService } from "./services/api/api";
import { Logger, LogLevel } from "./utils/logger";
import { WebAppService } from "./services/api/web-app";

export class SDK {
  #logger: Logger;
  #state: SDKState;
  #embedService: EmbedService;
  #loyaltyService: LoyaltyService;
  #tracker: SDKTracker;
  #toastNotificationManager: ToastNotificationManager;
  #authService: AuthService;
  #webAppService: WebAppService;

  static embedElementId = EMBED_ELEMENT_ID;
  static embedContainerId = EMBED_CONTAINER_ID;

  constructor(
    logger: Logger = new Logger("[FanfestSDK]", {
      logLevel: LogLevel.DEBUG,
    }),
    state: SDKState = new SDKState(logger),
    embedService: EmbedService = new EmbedService(state, logger),
    loyaltyService: LoyaltyService = new LoyaltyService(state, logger),
    tracker: SDKTracker = new SDKTracker(logger),
    toastNotificationManager: ToastNotificationManager = new ToastNotificationManager(
      logger,
      state,
    ),
    apiService: ApiService = new ApiService(state, logger),
    authService: AuthService = new AuthService(state, apiService, logger),
    webAppService: WebAppService = new WebAppService(apiService, logger),
  ) {
    this.#logger = logger;
    this.#state = state;
    this.#embedService = embedService;
    this.#loyaltyService = loyaltyService;
    this.#tracker = tracker;
    this.#toastNotificationManager = toastNotificationManager;
    this.#authService = authService;
    this.#webAppService = webAppService;
  }

  init = async (options: unknown): Promise<void> => {
    if (this.#state.isInitialized) {
      this.#logger.warn("Already initialized");
      return;
    }

    this.#logger.info("Initializing SDK", { options });

    const sdkOptions = new SDKOptions(options, this.#logger);

    this.#state.init(sdkOptions);
    this.#toastNotificationManager.init();
    this.#embedService.init();
    this.#authService.init();

    // Setup Tracker at last to ensure all elements are tracked
    this.#tracker.init();
    this.#tracker.onTrackEvent.subscribe((event) =>
      this.trackEvent(event.payload),
    );

    if (this.#state.channelInfo.channelId === null) {
      try {
        const webApp = await this.#webAppService.getWebApp();

        this.#logger.info("App info retrieved", { webApp });
        this.#state.channelInfo.value.set(webApp);
      } catch (error) {
        this.#logger.error("Failed to get web app", { error });
      }
    }

    this.#logger.info("SDK initialized");
  };

  registerActionMapping = (payload: SDKActionRegistryPayload): void => {
    this.#logger.debug("Registering action mapping", { payload });
    this.#state.actionRegistry.registerActionMapping(payload);
  };

  trackEvent = async (
    payload: TrackEventPayload,
  ): Promise<TrackEventResponse> => {
    const response = await this.#loyaltyService.trackEvent(payload);

    if (
      response.success &&
      (!this.#state.authState.hasUser || response.didReward)
    ) {
      showLoyaltyToastNotification(
        this.#toastNotificationManager,
        response,
        this.#state,
      );
    }

    return response;
  };

  login = async () => {
    this.#logger.info("Logging in");
    this.#authService.initiateSilentAuthentication();
  };

  logout = async () => {
    this.#logger.info("Logging out");
    this.#state.authState.value.remove();
  };
}
